import React from 'react';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import { ModalActionError } from '../../ui/ModalActionError';
import './EditorDialog.css';

type Props = { open: boolean; title: string; onClose: () => void; children: React.ReactNode; status?: React.ReactNode; compact?: boolean;
  /** Explicit owner for dialogs opened after async preparation disables the trigger. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string; footerActions?: React.ReactNode; onKeyDown?: React.KeyboardEventHandler<HTMLDialogElement>; busy?: boolean;
  closeOnBackdrop?: boolean; closeLabel?: string;
  /** false drops the footer's close button and leaves the header × as the only way out (edit-style-guide.md). */
  footerClose?: boolean;
  /** 'side' opens it as a full-height drawer on the right, e.g. for editing one row of a settings table. */
  placement?: 'center' | 'side';
  /** A short sentence-case line above the title, e.g. "Availability". Not part of the dialog's name. */
  kicker?: string;
  /** Detail under the title, e.g. an item's code and make. Not part of the dialog's name. */
  subtitle?: React.ReactNode;
  /** A failed save, shown above the footer next to the actions (ModalActionError takes focus). */
  error?: string | null;
  /** The error's heading, e.g. "The calendar entry was not saved". */
  errorTitle?: string;
  /** One recovery button inside the error, e.g. reloading a record someone else changed. */
  errorAction?: { label: string; onClick: () => void; disabled?: boolean } };

/** Keep editors mounted so closing a native modal never discards their drafts. */
export default function EditorDialog({ open, title, onClose, children, status, compact = false, className = '', footerActions, onKeyDown, busy = false, closeOnBackdrop = false, closeLabel = 'Done', footerClose = true, kicker, subtitle, placement = 'center', error, errorTitle, errorAction, returnFocusRef }: Props) {
  const ref = React.useRef<HTMLDialogElement>(null);
  const heading = React.useRef<HTMLHeadingElement>(null);
  const body = React.useRef<HTMLDivElement>(null);
  const id = React.useId();
  useModalScrollLock(open);
  React.useEffect(() => {
    if (!open) return;
    const previous = returnFocusRef?.current ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    const dialog = ref.current;
    dialog?.showModal();
    return () => { dialog?.close(); if (previous?.isConnected) previous.focus({ preventScroll: true }); };
  }, [open, returnFocusRef]);
  React.useEffect(() => {
    if (!open) return;
    body.current?.scrollTo({ top: 0 });
    heading.current?.focus({ preventScroll: true });
  }, [open, title]);
  return <dialog ref={ref} className={`editor-dialog ${className}`.trim()} data-compact={compact} data-placement={placement} aria-labelledby={id} aria-busy={busy} onKeyDown={onKeyDown} onClick={event => {
    if (!closeOnBackdrop || busy || event.target !== event.currentTarget) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
  }} onCancel={(event) => { event.preventDefault(); event.stopPropagation(); if (!busy) onClose(); }}>
    <header className="editor-dialog__header">{kicker || subtitle ? (
      <div className="editor-dialog__heading">
        {kicker ? <p className="editor-dialog__kicker">{kicker}</p> : null}
        <h3 ref={heading} tabIndex={-1} id={id}>{title}</h3>
        {subtitle ? <div className="editor-dialog__subtitle">{subtitle}</div> : null}
      </div>
    ) : <h3 ref={heading} tabIndex={-1} id={id}>{title}</h3>}<button type="button" className="btn-close" aria-label={`Close ${title}`} disabled={busy} onClick={onClose}>×</button></header>
    <div ref={body} className="editor-dialog__body">{children}</div>
    <ModalActionError message={error ?? null} title={errorTitle} actionLabel={errorAction?.label} onAction={errorAction?.onClick} actionDisabled={errorAction?.disabled} />
    <footer className="editor-dialog__footer"><div role="status" aria-live="polite">{status}</div>{footerActions}{footerClose ? <button type="button" className="btn btn-outline" disabled={busy} onClick={onClose}>{closeLabel}</button> : null}</footer>
  </dialog>;
}
